// src/pages/Condo/RankingCondo.js

import React, { useEffect, useState } from 'react'
import {
    Card,
    CardContent,
    CardHeader,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    CircularProgress,
    Box,
} from '@mui/material'
import { api } from '../../../services/Main/Api'
import moment from 'moment'

const formatCurrency = (value) => {
    if (!value) return 'R$ 0,00'
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
    }).format(value)
}

/**
 * Props:
 *  - startDate: data inicial (string, Date ou moment)
 *  - endDate: data final (string, Date ou moment)
 */
const RankingCondo = ({ startDate, endDate }) => {
    const [data, setData] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)

    useEffect(() => {
        const fetchRanking = async () => {
            // só busca se as duas datas existirem
            if (!startDate || !endDate) {
                setData([])
                return
            }

            const start = moment(startDate)
            const end = moment(endDate)

            if (!start.isValid() || !end.isValid()) {
                setError('Período inválido.')
                setData([])
                return
            }

            setLoading(true)
            setError(null)

            try {
                const response = await api.get('condo/ranking/', {
                    params: {
                        start_date: start.format('YYYY-MM-DD'),
                        end_date: end.format('YYYY-MM-DD'),
                    },
                })

                setData(response.data)
            } catch (err) {
                console.error(err)
                setError('Falha ao carregar ranking dos clientes.')
            } finally {
                setLoading(false)
            }
        }

        fetchRanking()
    }, [startDate, endDate])

    const startMoment = startDate ? moment(startDate) : null
    const endMoment = endDate ? moment(endDate) : null

    return (
        <Card>
            <CardHeader
                title="Ranking de Clientes"
                subheader={
                    startMoment && endMoment && startMoment.isValid() && endMoment.isValid()
                        ? `Período: ${startMoment.format('DD/MM/YYYY')} até ${endMoment.format('DD/MM/YYYY')}`
                        : 'Período não selecionado'
                }
            />
            <CardContent>
                {loading && (
                    <Box display="flex" justifyContent="center" mt={4}>
                        <CircularProgress />
                    </Box>
                )}

                {!loading && error && (
                    <Typography color="error" mt={2}>
                        {error}
                    </Typography>
                )}

                {!loading && !error && (!data || data.length === 0) && (
                    <Typography mt={2}>
                        Nenhum dado encontrado para o período selecionado.
                    </Typography>
                )}

                {!loading && !error && data && data.length > 0 && (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>#</TableCell>
                                <TableCell>Nome</TableCell>
                                <TableCell>Criado em</TableCell>
                                <TableCell align="right">Contratos</TableCell>
                                <TableCell align="right">Faturas (R$)</TableCell>
                                <TableCell align="right">Planos (R$)</TableCell>
                                <TableCell align="right">Taxas (R$)</TableCell>
                                <TableCell align="right">Total (R$)</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.map((condo, index) => (
                                <TableRow
                                    hover
                                    key={condo.id}>
                                    <TableCell sx={{ fontWeight: 'bold' }}>
                                        {index + 1}
                                    </TableCell>
                                    <TableCell>{condo.name}</TableCell>
                                    <TableCell>
                                        {condo.created_at
                                            ? moment(condo.created_at).format('DD/MM/YYYY')
                                            : '-'}
                                    </TableCell>
                                    <TableCell align="right">
                                        {condo.total_contract}
                                    </TableCell>
                                    <TableCell align="right">
                                        {formatCurrency((condo.total_invoice || 0) / 100)}
                                    </TableCell>
                                    <TableCell align="right">
                                        {formatCurrency((condo.total_plan_b3 || 0) / 100)}
                                    </TableCell>
                                    <TableCell align="right">
                                        {formatCurrency((condo.total_b3_fee || 0) / 100)}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                        {formatCurrency((condo.total_b3 || 0) / 100)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </CardContent>
        </Card>
    )
}

export default RankingCondo
